import * as Sentry from "@sentry/react";

/**
 * Sentry DSN (Data Source Name) for error tracking.
 *
 * SECURITY NOTE: DSNs are safe to keep public and can be hardcoded in client-side code.
 * They only allow submission of new events and do not provide read access to any data.
 *
 * From Sentry documentation:
 * "DSNs are safe to keep public because they only allow submission of new events
 * and related event data; they do not allow read access to any information."
 *
 * If abuse occurs, DSNs can be rotated in Sentry project settings under:
 * [Project] > Settings > SDK Setup > Client Keys (DSN)
 */
const SENTRY_DSN = "https://562b03ee1a0e17fa07a6cb228435f586@o4509684240941056.ingest.us.sentry.io/4509905938743296";

export type SentryConfig = {
    dsn: string;
    environment?: string;
    enabled: boolean;
};

/**
 * Initialize Sentry client
 * In local development, Sentry is disabled by default unless explicitly enabled
 * In production, Sentry is always initialized for error reporting infrastructure
 * @param config - Sentry configuration. If null, will automatically get config from environment variables
 */
export function initSentry(config: SentryConfig | null = null): void {
    const sentryConfig = config || getSentryConfig();

    if (!sentryConfig.enabled) {
        return;
    }

    Sentry.init({
        dsn: sentryConfig.dsn,
        environment: sentryConfig.environment || "development",
        // Disable all default integrations to prevent automatic error capture.
        // Exceptions must be captured explicitly. We can work on this separately.
        integrations: [],
        // Disable performance monitoring for now.
        tracesSampleRate: 0.0,
    });
}

/**
 * Get Sentry configuration
 */
export function getSentryConfig(): SentryConfig {
    // In development, Sentry is disabled by default
    // Can be enabled by setting VITE_SENTRY_ENABLED=true
    const isDevelopment = process.env.NODE_ENV === "development";
    const isTest = process.env.NODE_ENV === "test";
    const explicitlyEnabled = import.meta.env.VITE_SENTRY_ENABLED === "true";

    return {
        dsn: SENTRY_DSN,
        environment: process.env.NODE_ENV || "development",
        enabled: !isTest && (!isDevelopment || explicitlyEnabled),
    };
}
